import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GitBranch } from "lucide-react";
import api, { API, gbp } from "@/lib/api";
import { fmtDate, parseDate } from "@/components/DatePicker";
const fd = (s) => { const d = parseDate(s); return d ? fmtDate(d) : (s || "—"); };
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EngagerSetupCard from "@/components/EngagerSetupCard";
import ClearanceCard from "@/components/ClearanceCard";
import OnboardingCard from "@/components/OnboardingCard";
import ProposalVideoManager from "@/components/ProposalVideoManager";
import ProposalReminderCard from "@/components/ProposalReminderCard";
import ActivityTimeline from "@/components/ActivityTimeline";
import { XeroResyncDialog } from "@/components/XeroResyncDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import {
  Download, Send, Link2, XCircle, RefreshCw, ShieldCheck, Clock, CheckCircle2, FileText, AlertTriangle, FileSignature,
  Mail, MailCheck, MailOpen, MousePointerClick, AlertOctagon, Eye, RotateCcw,
} from "lucide-react";

const EMAIL_EVENTS = {
  sent: { label: "Sent", Icon: Mail, tone: "text-cass-slate" },
  email_delivered: { label: "Delivered", Icon: MailCheck, tone: "text-emerald-600" },
  email_opened: { label: "Opened", Icon: MailOpen, tone: "text-emerald-600" },
  email_clicked: { label: "Link clicked", Icon: MousePointerClick, tone: "text-cass-teal" },
  email_delayed: { label: "Delivery delayed", Icon: Clock, tone: "text-amber-600" },
  email_bounced: { label: "Bounced", Icon: AlertOctagon, tone: "text-red-500" },
  email_complained: { label: "Marked as spam", Icon: AlertOctagon, tone: "text-red-500" },
};

const STATUS_TONE = {
  draft: "secondary", sent: "default", viewed: "default", accepted: "default",
  rejected: "destructive", superseded: "outline", withdrawn: "outline",
};
const STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  sent: "bg-cass-teal/15 text-cass-teal border-cass-teal/30",
  viewed: "bg-cass-teal/20 text-cass-teal border-cass-teal/40",
  acceptance_started: "bg-cass-teal/25 text-cass-teal border-cass-teal/50",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-red-100 text-red-700 border-red-300",
  expired: "bg-red-50 text-red-600 border-red-200",
  superseded: "bg-slate-100 text-slate-500 border-slate-200",
  withdrawn: "bg-slate-100 text-slate-500 border-slate-200",
};

const SB_STAGES = [["draft", "Draft"], ["sent", "Sent"], ["viewed", "Viewed"], ["awaiting", "Awaiting signature"], ["accepted", "Accepted"]];
const SB_RAMP = ["#CFE9E7", "#9BDAD6", "#5DC3BE", "#2E9E99", "#0E7C77"];

// Per-proposal chevron funnel mirroring the dashboard proposal pipeline (full, not abbreviated).
function StageBar({ proposal }) {
  const acts = proposal.activity || [];
  const status = proposal.status;
  const has = (ev) => acts.some((a) => a.event === ev);
  let reached = 0;
  if (has("sent") || ["sent", "viewed", "acceptance_started", "accepted", "rejected", "expired", "withdrawn", "superseded"].includes(status)) reached = 1;
  if (has("first_viewed") || ["viewed", "acceptance_started", "accepted"].includes(status)) reached = 2;
  if (has("acceptance_started") || ["acceptance_started", "accepted"].includes(status)) reached = 3;
  if (status === "accepted") reached = 4;
  const terminal = ["rejected", "expired", "withdrawn", "superseded"].includes(status);
  const n = SB_STAGES.length;
  return (
    <div className="flex" data-testid="proposal-detail-stageflow">
      {SB_STAGES.map(([key, label], i) => {
        const done = i <= reached;
        const termLast = i === n - 1 && terminal;
        let bg = done ? SB_RAMP[i] : "#EEF2F6";
        let lbl = label;
        if (termLast) { bg = status === "rejected" ? "#DC2626" : "#94A3B8"; lbl = status.charAt(0).toUpperCase() + status.slice(1); }
        const white = termLast || (done && i >= 2);
        const textCls = (done || termLast) ? (white ? "text-white" : "text-cass-navy") : "text-cass-slate/60";
        return (
          <div key={key} data-testid={`detail-stage-${key}${done ? "-done" : ""}`}
            className={`relative flex-1 h-14 flex items-center justify-center px-2 text-center ${textCls}`}
            style={{ background: bg, marginLeft: i ? -14 : 0, clipPath: i === 0 ? "polygon(0 0,calc(100% - 14px) 0,100% 50%,calc(100% - 14px) 100%,0 100%)" : "polygon(0 0,calc(100% - 14px) 0,100% 50%,calc(100% - 14px) 100%,0 100%,14px 50%)" }}>
            <span className="text-xs font-medium leading-tight whitespace-pre">{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [ob, setOb] = useState(null);
  const [checks, setChecks] = useState(null);
  const [disc, setDisc] = useState({ label: "Discount", type: "percent", value: "", reason: "" });
  const [loe, setLoe] = useState(null);
  const [cap, setCap] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manual, setManual] = useState({ name: "", job_title: "", company: "", reason: "", mark_loe_signed: true });
  const [schedOpen, setSchedOpen] = useState(false);
  const [scheds, setScheds] = useState([]);
  const [schedAvail, setSchedAvail] = useState([]);
  const [schedActive, setSchedActive] = useState(new Set());
  const [schedLocked, setSchedLocked] = useState(false);
  const [allTests, setAllTests] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [viewEmail, setViewEmail] = useState(null);
  const [resendForm, setResendForm] = useState(null); // { to, name, subject }
  const [resendBusy, setResendBusy] = useState(false);
  const doResendCopy = async () => {
    if (!resendForm?.to?.trim()) return toast.error("Recipient email required");
    setResendBusy(true);
    try {
      const { data } = await api.post(`/proposals/${id}/sent-emails/${viewEmail.id}/resend`,
        { recipient_email: resendForm.to.trim(), recipient_name: resendForm.name || null, subject: resendForm.subject || null });
      toast.success(data.email_sent ? "Email re-sent" : "Copy saved (email not delivered)");
      setResendForm(null); setViewEmail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setResendBusy(false); }
  };
  const openScheds = async () => {
    const { data } = await api.get(`/proposals/${id}/loe/schedules`);
    setScheds(data.schedules || []);
    setSchedAvail(data.available || []);
    setSchedActive(new Set((data.available || []).filter((s) => s.active).map((s) => s.id)));
    setSchedLocked(!!data.locked);
    setSchedOpen(true);
  };
  const toggleSchedActive = (sid) => setSchedActive((prev) => { const n = new Set(prev); if (n.has(sid)) n.delete(sid); else n.add(sid); return n; });
  const saveScheds = async () => {
    const overrides = {}; scheds.forEach((s) => { overrides[s.id] = s.html; });
    const removed_ids = schedAvail.filter((s) => s.auto && !schedActive.has(s.id)).map((s) => s.id);
    const added_ids = schedAvail.filter((s) => !s.auto && schedActive.has(s.id)).map((s) => s.id);
    await api.put(`/proposals/${id}/loe/schedules`, { overrides, added_ids, removed_ids });
    toast.success("Schedules tailored for this proposal"); setSchedOpen(false); load();
  };
  useEffect(() => { if (p?.discount) setDisc({ label: p.discount.label, type: p.discount.type, value: p.discount.value, reason: p.discount.reason || "" }); }, [p?.id]);
  const saveDiscount = async () => { await api.put(`/proposals/${id}/discount`, { ...disc, value: Number(disc.value) || 0 }); toast.success(Number(disc.value) > 0 ? "Discount applied" : "Discount removed"); load(); };
  useEffect(() => { api.get("/testimonials").then((r) => setAllTests(r.data.filter((t) => t.active))).catch(() => {}); }, []);
  const toggleProposalTest = async (tid) => {
    const cur = p.testimonial_ids || [];
    const next = cur.includes(tid) ? cur.filter((x) => x !== tid) : [...cur, tid];
    setP({ ...p, testimonial_ids: next });
    try { await api.put(`/proposals/${id}`, { testimonial_ids: next }); } catch { toast.error("Could not save selection"); load(); }
  };
  const saveCap = async () => { await api.put(`/proposals/${id}/loe`, { liability_cap: cap }); toast.success("Liability cap saved"); loadLoe(); };
  const loadLoe = () => api.get(`/proposals/${id}/loe`).then((r) => { setLoe(r.data); setCap(r.data.liability_cap || ""); }).catch(() => {});
  const resend = async () => {
    try { await api.post(`/proposals/${id}/send`, { recipient_email: p.recipient_email, recipient_name: p.recipient_name, message: p.personal_message || "" }); toast.success("Proposal re-sent"); load(); }
    catch { toast.error("Could not resend"); }
  };
  const resendSigner = async (email) => {
    try { const { data } = await api.post(`/proposals/${id}/signers/resend`, { email }); toast.success(data.email_sent ? "Reminder sent" : "Link refreshed (email not delivered)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };
  const chaseAll = null;
  const voidReissue = async () => {    if (!(await confirmDialog({ title: "Void & re-issue?", description: "This withdraws the current signing links (any partial signatures are cleared) and emails fresh links to every signatory. Use when a multi-signer proposal has stalled.", confirmText: "Void & re-issue", tone: "destructive" }))) return;
    try { const { data } = await api.post(`/proposals/${id}/void-reissue`); toast.success(`Re-issued fresh links to ${data.reissued} signatories`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not re-issue"); }
  };
  const [xeroPrev, setXeroPrev] = useState(null);
  const [xeroBusy, setXeroBusy] = useState(false);
  const [xc, setXc] = useState(null);
  const loadContacts = async () => { try { const { data } = await api.get(`/xero/contact-search/${id}`); setXc(data); } catch { setXc(null); } };
  const openXeroPreview = async () => {
    try { const { data } = await api.get(`/xero/preview/${id}`); setXeroPrev(data); loadContacts(); }
    catch { toast.error("Could not build the Xero review"); }
  };
  const linkContact = async (contactId) => {
    if (!xc?.client_id) return;
    try { await api.post(`/xero/link-contact/${xc.client_id}`, { contact_id: contactId }); toast.success("Xero contact linked"); loadContacts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link contact"); }
  };
  const createContact = async () => {
    if (!xc?.client_id) return;
    try { await api.post(`/xero/create-contact/${xc.client_id}`); toast.success("Xero contact created"); loadContacts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create contact"); }
  };
  const pushContact = async () => {
    if (!xc?.client_id) return;
    try { const { data } = await api.post(`/xero/push-contact/${xc.client_id}`); toast.success(data.updated ? "Xero contact details updated" : "Xero contact created"); loadContacts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update Xero contact"); }
  };
  const createXero = async (only) => {
    setXeroBusy(true);
    try {
      const { data } = await api.post(`/xero/sync/${id}?only=${only}`);
      const wantRep = only === "repeating" || only === "both";
      const wantOo = only === "oneoff" || only === "both";
      if (wantRep && data.repeating_error) toast.error(`Repeating invoice failed: ${data.repeating_error}`);
      else if (wantRep && data.repeating_invoice_id) toast.success(data.is_variation ? "Repeating invoice updated in Xero" : "Repeating invoice created in Xero");
      if (wantOo && data.oneoff_error) toast.error(`One-off draft invoice failed: ${data.oneoff_error}`);
      else if (wantOo && data.invoice_id) toast.success("Draft one-off invoice created in Xero");
      try { const { data: pv } = await api.get(`/xero/preview/${id}`); setXeroPrev(pv); } catch { /* keep dialog */ }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Xero action failed — connect Xero in Admin → Integrations"); }
    finally { setXeroBusy(false); }
  };

  const load = () =>
    api.get(`/proposals/${id}`).then((r) => {      setP(r.data);
      loadLoe();
      api.get(`/proposals/${id}/sent-emails`).then((s) => setSentEmails(s.data.items || [])).catch(() => {});
      if (r.data.status === "accepted" && r.data.proposal_type === "new")
        api.get(`/proposals/${id}/onboarding`).then((o) => setOb(o.data)).catch(() => {});
    });
  useEffect(() => { load(); }, [id]);

  if (!p) return <div className="p-8 text-cass-slate">Loading…</div>;

  const link = p.token ? `${window.location.origin}/p/${p.token}` : null;
  const isRenewal = p.proposal_type === "renewal";

  const copyLink = () => { navigator.clipboard.writeText(link); toast.success("Secure link copied"); };
  const raiseVariation = async () => {
    try { const { data } = await api.post(`/proposals/${id}/raise-variation`); toast.success(`Variation ${data.reference} created`); navigate(`/proposals/${data.id}/edit`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not raise variation"); }
  };
  const downloadPdf = () => window.open(`${API}/proposals/${id}/pdf`, "_blank");
  const reversal = [...(p.activity || [])].reverse().find((a) => a.event === "acceptance_reversed");
  // Task 2: on accepted/superseded proposals, split the internal breakdown into what the client
  // signed off (recommended/included + selected optionals) vs optionals they declined.
  const isSignedOff = p.status === "accepted" || p.status === "superseded";
  const selectedOptionalIds = new Set(p.selected_optionals || []);
  const presById = Object.fromEntries((p.services || []).map((s) => [s.service_id, s.presentation]));
  const isDeclinedOptional = (sid) => presById[sid] === "optional" && !selectedOptionalIds.has(sid);
  const acceptedBreakdowns = isSignedOff ? (p.breakdowns || []).filter((b) => !isDeclinedOptional(b.service_id)) : (p.breakdowns || []);
  const declinedBreakdowns = isSignedOff ? (p.breakdowns || []).filter((b) => isDeclinedOptional(b.service_id)) : [];
  const runChecks = async () => { const { data } = await api.get(`/proposals/${id}/validate`); setChecks(data); };
  const withdraw = async () => { await api.post(`/proposals/${id}/withdraw`); toast.success("Withdrawn"); load(); };
  const revise = async () => {
    if (!(await confirmDialog({ title: "Revise this proposal?", description: "Creates a new internal version (e.g. v2) and reverts it to draft so you can amend it and re-send. The client only ever sees the proposal number — the version is internal and recorded in the audit history.", confirmText: "Revise" }))) return;
    try { const { data } = await api.post(`/proposals/${id}/revise`); toast.success(`Revised to v${data.version}`); navigate(`/proposals/${id}/edit`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revise"); }
  };
  const revertRevise = async () => {
    if (!(await confirmDialog({ title: "Undo the last revise?", description: "Restores this proposal exactly as it was just before you revised it — status, fees/totals, selected extras and schedules. Any edits made since revising will be discarded.", confirmText: "Undo revise" }))) return;
    try { const { data } = await api.post(`/proposals/${id}/revert-revise`); toast.success(`Restored to v${data.version}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not undo revise"); }
  };

  const undoAccept = async () => {
    if (!(await confirmDialog({ title: "Reverse manual acceptance?", description: "This restores the proposal's prior status, removes the manual LoE signature and onboarding record, and reverts the client. Any Xero invoices already created must be voided in Xero manually.", tone: "destructive", confirmText: "Reverse" }))) return;
    try {
      const { data } = await api.post(`/proposals/${id}/undo-accept`);
      toast.success(`Acceptance reversed — proposal restored to "${data.status}"${data.xero_synced ? " · remember to void the Xero invoice(s)" : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reverse acceptance"); }
  };

  const submitManualAccept = async () => {
    if (!manual.name.trim() || !manual.reason.trim()) { toast.error("Client name and a reason are required"); return; }
    setManualBusy(true);
    try {
      const { data } = await api.post(`/proposals/${id}/manual-accept`, manual);
      toast.success(`Proposal marked as accepted${data.loe_signed ? " · LoE marked signed" : ""}`);
      setManualOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark as accepted"); }
    finally { setManualBusy(false); }
  };

  const toggleCheck = async (key) => {
    const checklist = { ...ob.checklist, [key]: !ob.checklist[key] };
    const { data } = await api.put(`/onboarding/${ob.id}`, { checklist });
    setOb(data);
  };

  return (
    <div>
      <PageHeader
        title={<span className="flex items-center gap-3">{p.reference}{p.version > 1 ? ` v${p.version}` : ""} <Badge variant="outline" className={STATUS_CLASS[p.status] || ""}>{p.status}</Badge> <Badge variant="outline">{p.proposal_type}</Badge>{p.delivery_status?.state === "link_only"
          ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" data-testid="delivery-status-badge"><Link2 className="w-3 h-3 mr-1" />Link only · not emailed</Badge>
          : p.delivery_status?.state && <Badge variant="outline" className="capitalize" data-testid="delivery-status-badge">Email: {p.delivery_status.state}</Badge>}{p.xero_sync && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" title={`Repeating invoice ${p.reference}${p.xero_sync.repeating_invoice_id ? " · " + p.xero_sync.repeating_invoice_id : " (contact only)"}`} data-testid="xero-synced-badge">Synced to Xero &#10003;{p.xero_sync.repeating_invoice_id ? ` · ${p.reference}` : ""}</Badge>}</span>}
        subtitle={`${p.client_name} · v${p.version} · owner ${p.owner_name}`}
        actions={
          <>
            <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={() => window.open(`${API}/proposals/${id}/internal-pdf`, "_blank")} data-testid="internal-pdf-btn"><FileText className="w-4 h-4 mr-2" />Internal PDF</Button>
            <Button variant="outline" onClick={() => window.open(`${API}/proposals/${id}/loe/pdf`, "_blank")} data-testid="view-loe-btn"><FileSignature className="w-4 h-4 mr-2" />LoE</Button>
            {p.status !== "accepted" && p.status !== "superseded" && <Button variant="outline" onClick={() => navigate(`/proposals/${id}/document`)} data-testid="edit-document-btn"><FileText className="w-4 h-4 mr-2" />Document</Button>}
            {p.status !== "accepted" && p.status !== "superseded" && <Button variant="outline" onClick={runChecks} data-testid="run-checks-btn"><AlertTriangle className="w-4 h-4 mr-2" />Checks</Button>}
            {p.status === "draft" && p.revise_undo && <Button variant="outline" onClick={revertRevise} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="revert-revise-btn"><RotateCcw className="w-4 h-4 mr-2" />Undo revise</Button>}
            {p.status === "draft" && <Button onClick={() => navigate(`/proposals/${id}/edit`)} className="bg-cass-navy text-white" data-testid="edit-proposal-btn"><Send className="w-4 h-4 mr-2" />Edit & Send</Button>}
            {link && p.status !== "accepted" && p.status !== "superseded" && <Button variant="outline" onClick={copyLink} data-testid="copy-link-btn"><Link2 className="w-4 h-4 mr-2" />Copy link</Button>}
            {["sent", "viewed"].includes(p.status) && <Button variant="outline" onClick={resend} data-testid="resend-btn"><Send className="w-4 h-4 mr-2" />Resend</Button>}
            {["sent", "viewed", "acceptance_started"].includes(p.status) && <Button variant="outline" onClick={revise} data-testid="revise-btn"><GitBranch className="w-4 h-4 mr-2" />Revise</Button>}
            {["sent", "viewed"].includes(p.status) && <Button variant="outline" onClick={withdraw} data-testid="withdraw-btn"><XCircle className="w-4 h-4 mr-2" />Withdraw</Button>}
            {!["accepted", "superseded", "withdrawn"].includes(p.status) && <Button onClick={() => setManualOpen(true)} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="manual-accept-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Mark as accepted</Button>}
            {p.status === "accepted" && p.signature?.manual && <Button variant="outline" onClick={undoAccept} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="undo-accept-btn"><XCircle className="w-4 h-4 mr-2" />Undo acceptance</Button>}
            {p.status === "accepted" && <Button onClick={() => navigate(`/proposals/new?type=renewal&client=${p.client_id}&renewalOf=${p.id}`)} className="bg-cass-teal text-white" data-testid="renew-btn"><RefreshCw className="w-4 h-4 mr-2" />Create Renewal</Button>}
            {p.status === "accepted" && <Button variant="outline" onClick={raiseVariation} data-testid="raise-variation-btn"><GitBranch className="w-4 h-4 mr-2" />Raise variation</Button>}
            {p.status === "accepted" && <Button variant="outline" onClick={openXeroPreview} data-testid="xero-create-btn"><FileText className="w-4 h-4 mr-2" />Create Xero Invoices</Button>}
            {p.status === "accepted" && (
              <XeroResyncDialog proposalId={id} reference={p.reference} onDone={load}
                trigger={<Button variant="outline" data-testid="xero-resync-detail-btn"><RefreshCw className="w-4 h-4 mr-2" />Re-sync / invoice</Button>} />
            )}
            <Dialog open={!!xeroPrev} onOpenChange={(o) => !o && setXeroPrev(null)}>
              <DialogContent className="max-w-2xl" data-testid="xero-preview-dialog">
                <DialogHeader><DialogTitle>Create Xero Invoices</DialogTitle></DialogHeader>
                {xeroPrev && (
                  <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-3 gap-2 text-xs bg-cass-bone rounded-md p-3">
                      <div><div className="text-cass-slate">Client</div><div className="text-cass-navy font-semibold">{xeroPrev.contact_name}</div></div>
                      <div><div className="text-cass-slate">Proposal</div><div className="text-cass-navy font-semibold">{xeroPrev.reference}</div></div>
                      <div><div className="text-cass-slate">Xero contact</div><div className="text-cass-navy font-semibold">{xeroPrev.contact_name}</div></div>
                    </div>

                    {xc && !xc.linked_contact_id && (
                      <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs space-y-2" data-testid="xero-contact-picker">
                        <div className="text-amber-800 font-medium">No Xero contact linked yet — pick a match or create one to avoid duplicates.</div>
                        {xc.matches?.length > 0 ? xc.matches.map((m) => (
                          <div key={m.contact_id} className="flex items-center justify-between bg-white rounded px-2 py-1.5 border">
                            <div><span className="text-cass-navy">{m.name}</span>{m.email && <span className="text-cass-slate"> · {m.email}</span>}</div>
                            <Button size="sm" variant="outline" onClick={() => linkContact(m.contact_id)} data-testid="xero-link-contact">Use this</Button>
                          </div>
                        )) : <div className="text-cass-slate">No likely matches in Xero.</div>}
                        <Button size="sm" className="bg-cass-navy text-white" onClick={createContact} data-testid="xero-create-contact">Create new Xero contact from client details</Button>
                      </div>
                    )}
                    {xc?.linked_contact_id && <div className="flex items-center justify-between text-xs" data-testid="xero-contact-linked"><span className="text-emerald-700">Xero contact linked &#10003;</span><Button size="sm" variant="outline" onClick={pushContact} data-testid="xero-push-contact">Update Xero contact details (address etc.)</Button></div>}

                    {/* Monthly Repeating Invoice */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold text-cass-navy">{xeroPrev.repeating?.is_variation_update ? `Update repeating invoice${xeroPrev.repeating?.source_ref ? ` (from ${xeroPrev.repeating.source_ref})` : ""}` : "Monthly Repeating Invoice"}</div>
                        {xeroPrev.repeating?.already_created && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="xero-repeating-created">Already created &#10003;</Badge>}
                        {xeroPrev.repeating?.requires_review && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="xero-repeating-review">Requires review — Xero £{Number(xeroPrev.repeating.created_amount).toFixed(2)} vs proposal £{Number(xeroPrev.repeating.total).toFixed(2)}</Badge>}
                      </div>
                      {xeroPrev.repeating?.is_variation_update && xeroPrev.repeating?.old_amount != null && (
                        <div className="text-[11px] text-cass-slate mb-1" data-testid="xero-repeating-oldnew">Monthly recurring: £{Number(xeroPrev.repeating.old_amount).toFixed(2)} → £{Number(xeroPrev.repeating.total).toFixed(2)} · updates the same Xero template</div>
                      )}
                      {xeroPrev.repeating?.service_count > 0 ? (
                        <div className="border border-border rounded-md" data-testid="xero-preview-repeating">
                          <div className="divide-y">
                            {(xeroPrev.repeating.lines || []).map((l, i) => (
                              <div key={i} className="flex items-start justify-between p-2.5 gap-3" data-testid={`xero-repeating-line-${i}`}>
                                <div><div className="text-cass-navy">{l.description}</div><div className="text-xs text-cass-slate">Account {l.account_code}{l.consolidated ? ` · consolidated from ${l.service_count} service(s)` : " · separate line"}</div></div>
                                <div className="text-cass-navy tabular whitespace-nowrap">{gbp(l.amount)}</div>
                              </div>
                            ))}
                          </div>
                          <div className="px-2.5 pt-1 text-[11px] text-cass-slate">Frequency Monthly · first invoice {xeroPrev.start_date}</div>
                          <div className="flex items-center justify-between p-2.5 bg-cass-bone text-xs">
                            <span className="text-cass-slate">Net {gbp(xeroPrev.repeating.total)} · VAT {gbp(xeroPrev.repeating.vat)}</span>
                            <span className="text-cass-navy font-semibold">Gross {gbp(xeroPrev.repeating.gross)} /mo</span>
                          </div>
                          {xeroPrev.xero_sync?.repeating_error && <div className="p-2 text-xs text-red-600" data-testid="xero-repeating-error">Last attempt failed: {xeroPrev.xero_sync.repeating_error}</div>}
                        </div>
                      ) : <p className="text-xs text-cass-slate">No monthly services — no repeating invoice needed.</p>}
                    </div>

                    {/* One-Off Draft Invoice */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold text-cass-navy">One-Off Draft Invoice</div>
                        {xeroPrev.oneoff_already_created && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="xero-oneoff-created">Already created &#10003;{xeroPrev.xero_sync?.invoice_number ? ` · ${xeroPrev.xero_sync.invoice_number}` : ""}</Badge>}
                        {xeroPrev.oneoff_requires_review && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="xero-oneoff-review">Requires review — Xero £{Number(xeroPrev.oneoff_created_amount).toFixed(2)} vs proposal £{Number(xeroPrev.oneoff_total).toFixed(2)}</Badge>}
                      </div>
                      {xeroPrev.oneoff_lines?.length > 0 ? (
                        <div className="border border-border rounded-md">
                          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-2.5 py-1.5 text-[11px] text-cass-slate border-b"><span>Description</span><span className="text-right">Amount</span><span className="text-right">VAT</span></div>
                          {xeroPrev.oneoff_lines.map((l, i) => (
                            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-2.5 py-2 border-b last:border-0 items-center" data-testid={`xero-preview-oneoff-${i}`}>
                              <div><div className="text-cass-navy">{l.description}</div><div className="text-[11px] text-cass-slate">Account {l.account_code}</div></div>
                              <div className="text-right text-cass-navy tabular">{gbp(l.unit_amount)}</div>
                              <div className="text-right text-cass-slate tabular">{gbp(l.vat)}</div>
                            </div>
                          ))}
                          <div className="px-2.5 py-2 bg-cass-bone text-xs space-y-0.5">
                            <div className="flex justify-between"><span className="text-cass-slate">Subtotal</span><span className="tabular text-cass-navy">{gbp(xeroPrev.oneoff_total)}</span></div>
                            <div className="flex justify-between"><span className="text-cass-slate">VAT</span><span className="tabular text-cass-navy">{gbp(xeroPrev.oneoff_vat)}</span></div>
                            <div className="flex justify-between font-semibold"><span className="text-cass-navy">Total</span><span className="tabular text-cass-navy">{gbp(xeroPrev.oneoff_gross)}</span></div>
                          </div>
                          {xeroPrev.xero_sync?.oneoff_error && <div className="p-2 text-xs text-red-600" data-testid="xero-oneoff-error">Last attempt failed: {xeroPrev.xero_sync.oneoff_error}</div>}
                        </div>
                      ) : <p className="text-xs text-cass-slate">No one-off or catch-up fees — no draft invoice needed.</p>}
                    </div>

                    <p className="text-[11px] text-cass-slate">The one-off invoice is created as <b>DRAFT</b> in Xero for your team to review and approve — it is never sent, emailed or marked paid automatically. Reference on both = <b>{xeroPrev.reference}</b>.</p>
                  </div>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={() => setXeroPrev(null)} data-testid="xero-preview-close">Close</Button>
                  <Button variant="outline" disabled={xeroBusy || !xeroPrev?.repeating?.service_count || xeroPrev?.repeating?.already_created} onClick={() => createXero("repeating")} data-testid="xero-create-repeating">{xeroPrev?.repeating?.is_variation_update ? "Update Repeating Invoice" : "Create Repeating Invoice"}</Button>
                  <Button variant="outline" disabled={xeroBusy || !xeroPrev?.oneoff_lines?.length || xeroPrev?.oneoff_already_created} onClick={() => createXero("oneoff")} data-testid="xero-create-oneoff">Create Draft One-Off Invoice</Button>
                  <Button className="bg-cass-navy text-white" disabled={xeroBusy || ((!xeroPrev?.repeating?.service_count || xeroPrev?.repeating?.already_created) && (!xeroPrev?.oneoff_lines?.length || xeroPrev?.oneoff_already_created))} onClick={() => createXero("both")} data-testid="xero-create-both"><RefreshCw className="w-4 h-4 mr-2" />Create Both in Xero</Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <div className="px-8 pt-4">
        <div className="bg-white border border-border rounded-xl px-5 py-4" data-testid="proposal-detail-stage-card">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate mb-3">Proposal stage</div>
          <StageBar proposal={p} />
        </div>
      </div>
      <div className="p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {reversal && p.status !== "accepted" && (
            <Card className="p-4 rounded-md bg-amber-50 border-amber-200 flex items-center gap-3" data-testid="reversal-note">
              <RefreshCw className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">
                A previous acceptance was reversed on <b>{new Date(reversal.at).toLocaleString("en-GB")}</b>
                {reversal.by ? <> by <b>{reversal.by}</b></> : null}. The proposal was restored to its earlier status.
              </span>
            </Card>
          )}
          {(p.status === "accepted" || p.status === "superseded") && (
            <Card className="p-4 rounded-md bg-emerald-50 border-emerald-200 flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-800">This document is locked and immutable. Any change requires a new proposal or renewal.</span>
            </Card>
          )}

          <Card className="p-6 rounded-md">
            <h3 className="font-alt font-semibold text-cass-navy mb-4">Internal pricing breakdown{isSignedOff && <span className="text-xs font-normal text-cass-slate ml-2">(client-selected & signed off)</span>}</h3>
            {acceptedBreakdowns.map((b) => (
              <div key={b.service_id} className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-cass-navy text-sm">{b.client_name}</span>
                  <span className="text-xs text-cass-slate">Rec {gbp(b.recommended_fee)} → Proposed {gbp(b.proposed_fee)} {b.difference !== 0 && <span className="text-cass-bronze">({b.adjustment_pct}%)</span>}</span>
                </div>
                <div className="space-y-0.5 text-xs pl-2 border-l-2 border-border">
                  {b.lines.map((l, i) => (
                    <div key={i} className="flex justify-between"><span className="text-cass-slate">{l.label}</span><span className="tabular">{gbp(l.amount)}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </Card>

          {declinedBreakdowns.length > 0 && (
            <Card className="p-6 rounded-md border-red-200 bg-red-50/60" data-testid="declined-optionals">
              <h3 className="font-alt font-semibold text-red-800 mb-1">Optional extras not selected</h3>
              <p className="text-xs text-red-700/80 mb-4">These optional services were offered but the client did not select them — they are excluded from the agreed total and Xero invoices.</p>
              {declinedBreakdowns.map((b) => (
                <div key={b.service_id} className="mb-4" data-testid={`declined-optional-${b.service_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-red-900 text-sm">{b.client_name}</span>
                    <span className="text-xs text-red-700/80">Rec {gbp(b.recommended_fee)} → Proposed {gbp(b.proposed_fee)} {b.difference !== 0 && <span>({b.adjustment_pct}%)</span>}</span>
                  </div>
                  <div className="space-y-0.5 text-xs pl-2 border-l-2 border-red-200">
                    {b.lines.map((l, i) => (
                      <div key={i} className="flex justify-between"><span className="text-red-700/80">{l.label}</span><span className="tabular text-red-800">{gbp(l.amount)}</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {p.status !== "accepted" && p.status !== "superseded" && (
            <Card className="p-6 rounded-md" data-testid="discount-card">
              <h3 className="font-alt font-semibold text-cass-navy mb-3">Discount (client-facing)</h3>
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="text-xs text-cass-slate">Label</label><Input className="w-40" value={disc.label} onChange={(e) => setDisc({ ...disc, label: e.target.value })} data-testid="discount-label" /></div>
                <div><label className="text-xs text-cass-slate">Type</label>
                  <select className="block border border-border rounded-md h-10 px-2 text-sm bg-white" value={disc.type} onChange={(e) => setDisc({ ...disc, type: e.target.value })} data-testid="discount-type"><option value="percent">% of net</option><option value="amount">£ amount</option></select>
                </div>
                <div><label className="text-xs text-cass-slate">Value</label><Input className="w-28" type="number" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} data-testid="discount-value" /></div>
                <Button onClick={saveDiscount} className="bg-cass-navy text-white" data-testid="save-discount-btn">Apply</Button>
                {p.discount && <span className="text-sm text-cass-teal">Applied: {p.discount.label} {p.discount.type === "percent" ? `${p.discount.value}%` : gbp(p.discount.value)}{p.discount.reason ? ` — ${p.discount.reason}` : ""}</span>}
              </div>
              <div className="mt-2"><label className="text-xs text-cass-slate">Reason (shown on the proposal)</label><Input value={disc.reason} onChange={(e) => setDisc({ ...disc, reason: e.target.value })} placeholder="e.g. Multi-service loyalty discount" data-testid="discount-reason" /></div>
              <p className="text-xs text-cass-slate mt-2">Shows as a discount line and reduces the totals on the client PDF and online proposal.</p>
            </Card>
          )}

          {p.status !== "accepted" && p.status !== "superseded" && allTests.length > 0 && (
            <Card className="p-6 rounded-md" data-testid="proposal-testimonials-card">
              <h3 className="font-alt font-semibold text-cass-navy mb-1">Testimonials on this proposal</h3>
              <p className="text-xs text-cass-slate mb-3">Tick the testimonials to feature for this client. Leave all unticked to show every active testimonial (default).</p>
              <div className="space-y-2">
                {allTests.map((t) => {
                  const checked = (p.testimonial_ids || []).includes(t.id);
                  return (
                    <label key={t.id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`ptest-${t.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProposalTest(t.id)} className="mt-1" data-testid={`ptest-check-${t.id}`} />
                      <span className="text-cass-slate">{t.quote ? `"${t.quote.slice(0, 90)}${t.quote.length > 90 ? "…" : ""}"` : "(image testimonial)"}{t.author ? <b className="text-cass-navy"> — {t.author}</b> : null}</span>
                    </label>
                  );
                })}
              </div>
              {(p.testimonial_ids || []).length > 0 && <button className="text-xs text-cass-teal mt-3" onClick={async () => { setP({ ...p, testimonial_ids: [] }); try { await api.put(`/proposals/${id}`, { testimonial_ids: [] }); } catch { toast.error("Could not reset"); } }} data-testid="ptest-clear">Reset to show all</button>}
            </Card>
          )}

          {p.status !== "superseded" && (
            <Card className="p-6 rounded-md" data-testid="proposal-video-card">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-alt font-semibold text-cass-navy">Personal video</h3>
                {p.video_played_at
                  ? <span className="flex items-center gap-2"><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="video-played-badge">Watched by client</Badge><span className="text-[11px] text-cass-slate" data-testid="video-played-at">{new Date(p.video_played_at).toLocaleString("en-GB")}</span></span>
                  : p.video ? <Badge variant="outline" className="text-cass-slate" data-testid="video-notplayed-badge">Not watched yet</Badge> : null}
              </div>
              <p className="text-xs text-cass-slate mb-3">Shows at the top of the client's online proposal. Optional — paste a Loom/YouTube/Vimeo link or upload a short clip. Doesn't affect the PDF or signed engagement.</p>
              <ProposalVideoManager proposalId={id} video={p.video} onChange={() => load()} />
            </Card>
          )}

          {(p.adjustment_log || []).length > 0 && (
            <Card className="p-6 rounded-md">
              <h3 className="font-alt font-semibold text-cass-navy mb-3">Manual adjustment history</h3>
              {p.adjustment_log.map((a, i) => (
                <div key={i} className="text-xs text-cass-slate border-b border-border py-2">
                  {a.from != null ? `${gbp(a.from)} → ` : ""}{gbp(a.to ?? a.amount)} (rec {gbp(a.recommended)}) · <span className="italic">"{a.reason}"</span> · {a.by} · {new Date(a.at).toLocaleString("en-GB")}
                </div>
              ))}
            </Card>
          )}

          {p.signature && (
            <Card className="p-6 rounded-md">
              <h3 className="font-alt font-semibold text-cass-navy mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" />Signature certificate</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["Signatory", p.signature.name], ["Job title", p.signature.job_title], ["Company", p.signature.company],
                  ["Email", p.signature.email], ["Signed (UTC)", p.signature.signed_at_utc], ["Local time", p.signature.signed_at_local],
                  ["Method", p.signature.method], ["IP address", p.signature.ip]].map(([k, v]) => (
                  <div key={k}><span className="text-cass-slate text-xs">{k}</span><div className="text-cass-navy">{v || "—"}</div></div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-cass-slate break-all">Document hash: {p.signature.document_hash}</div>
            </Card>
          )}

          {p.status === "accepted" && <OnboardingCard proposalId={id} />}

          {(p.signers || []).length > 1 && (
            <Card className="p-6 rounded-md" data-testid="signatory-register">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cass-teal" />Signatories ({(p.signers || []).filter((s) => s.signed).length}/{(p.signers || []).length}) · {p.signing_policy === "all" ? "all must sign" : "any one signs"}</h3>
                {(p.signers || []).some((s) => !s.signed) && p.status !== "accepted" && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={voidReissue} data-testid="void-reissue-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Void &amp; re-issue</Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {(p.signers || []).map((s) => (
                  <div key={s.email} className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2" data-testid={`signer-reg-${s.email}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-cass-navy">{s.name} <span className="text-cass-slate text-xs">· {s.email}</span></div>
                      {s.signed ? (
                        <div className="text-[11px] text-emerald-700">Signed {s.signature?.signed_at_local || ""}{s.signature?.ip ? ` · IP ${s.signature.ip}` : ""}</div>
                      ) : (
                        <div className="text-[11px] text-amber-600">Awaiting signature{s.last_reminded ? " · reminded" : ""}</div>
                      )}
                    </div>
                    {s.signed
                      ? <Badge variant="default" className="bg-emerald-600">Signed</Badge>
                      : (<Button size="sm" variant="outline" onClick={() => resendSigner(s.email)} data-testid={`resend-signer-${s.email}`}><Send className="w-3.5 h-3.5 mr-1" />Resend</Button>)}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {ob && (
            <Card className="p-6 rounded-md">
              <h3 className="font-alt font-semibold text-cass-navy mb-1">Onboarding checklist</h3>
              <p className="text-xs text-cass-slate mb-3">Work does not commence until these are complete.</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(ob.checklist).map(([k, v]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={v} onCheckedChange={() => toggleCheck(k)} data-testid={`onboard-${k}`} />
                    <span className="capitalize text-cass-slate">{k.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 rounded-md">
            <h3 className="font-alt font-semibold text-cass-navy mb-3">Summary</h3>
            <div className="text-sm space-y-1 text-cass-slate">
              <div>One-off fees: <span className="tabular" data-testid="summary-oneoff">{gbp((p.totals?.one_off || 0) - (p.totals?.fee_alignment_total || 0))}</span></div>
              {(() => {
                const d = p.discount;
                const monthly = Number(p.totals?.monthly_recurring) || 0;
                let amt = 0;
                if (d && Number(d.value) > 0) { amt = d.type === "percent" ? monthly * Number(d.value) / 100 : Number(d.value); amt = Math.min(amt, monthly); }
                const after = Math.round((monthly - amt) * 100) / 100;
                return (<>
                  <div>Monthly Fees: <span className="font-semibold text-cass-navy tabular" data-testid="summary-monthly">{gbp(after)}</span></div>
                  {amt > 0 && <div className="text-xs text-cass-teal" data-testid="summary-monthly-discount">incl. {p.discount.label} −{gbp(amt)}/mo (was {gbp(monthly)})</div>}
                </>);
              })()}
              <div>Catch-up fees: <span className="tabular" data-testid="summary-catchup">{gbp(p.totals?.fee_alignment_total)}</span></div>
              {p.alignment_plan?.schedule?.length > 0 && p.alignment_plan.method !== "one_off" && (
                <div className="text-xs text-cass-teal" data-testid="summary-catchup-plan">
                  {p.alignment_plan.instalments} month{p.alignment_plan.instalments === 1 ? "" : "s"} @ {gbp((Number(p.totals?.fee_alignment_total) || 0) / p.alignment_plan.instalments)} + VAT/mo ({p.alignment_plan.start_month} – {p.alignment_plan.end_month})
                </div>
              )}
              <div>Issue date: {fd(p.issue_date)}</div>
              <div>Expiry: {fd(p.expiry_date)}</div>
              {isRenewal && <div>Effective date: {fd(p.effective_date)}</div>}
            </div>
            {isRenewal && p.previous_totals && (
              <><Separator className="my-3" />
              <div className="text-sm text-cass-slate">Was {gbp(p.previous_totals.monthly_recurring)}/mo → {gbp(p.totals?.monthly_recurring)}/mo</div></>
            )}
          </Card>

          {["draft", "sent", "viewed", "acceptance_started"].includes(p.status) && (
            <ProposalReminderCard proposalId={id} canEdit={user?.role !== "read_only"} />
          )}

          <Card className="p-6 rounded-md" data-testid="loe-card">
            <h3 className="font-alt font-semibold text-cass-navy mb-3 flex items-center gap-2"><FileSignature className="w-4 h-4 text-cass-teal" />Letter of Engagement</h3>
            {loe && (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-cass-slate">Status</span>
                  <Badge variant={loe.status === "signed" ? "default" : loe.status === "awaiting_signature" ? "secondary" : "outline"} data-testid="loe-status">
                    {loe.status === "signed" ? "Signed & Active" : loe.status === "awaiting_signature" ? "Awaiting signature" : "Draft"}
                  </Badge>
                </div>
                {loe.schedule_names?.length > 0 && (
                  <div><span className="text-cass-slate text-xs">Auto-included schedules</span>
                    <div className="text-cass-navy text-xs mt-0.5">{loe.schedule_names.join(", ")}</div></div>
                )}
                <div>
                  <label className="text-xs text-cass-slate">Liability cap</label>
                  <div className="flex gap-2 mt-1">
                    <Input value={cap} onChange={(e) => setCap(e.target.value)} disabled={loe.status === "signed"} data-testid="loe-cap-input" />
                    {loe.status !== "signed" && <Button onClick={saveCap} className="bg-cass-navy text-white shrink-0" data-testid="save-cap-btn">Save</Button>}
                  </div>
                </div>
                {loe.signature && (
                  <div className="text-[11px] text-cass-slate border-t border-border pt-2">
                    Signed by {loe.signature.name} · {loe.signature.signed_at_local}
                    <div className="break-all mt-0.5">Checksum: {loe.signature.document_hash}</div>
                  </div>
                )}
                <Button variant="outline" className="w-full" onClick={() => window.open(`${API}/proposals/${id}/loe/pdf`, "_blank")} data-testid="loe-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />{loe.status === "signed" ? "Download signed LoE" : "View draft LoE"}
                </Button>
                {loe.status !== "signed" && (
                  <Button variant="ghost" className="w-full text-cass-slate" onClick={openScheds} data-testid="tailor-scheds-btn">
                    <FileText className="w-4 h-4 mr-2" />Tailor schedule wording
                  </Button>
                )}
              </div>
            )}
          </Card>

          {p.status === "accepted" && <EngagerSetupCard proposalId={id} />}

          {p.status === "accepted" && (
            <Card className="p-6 rounded-md" data-testid="xero-sync-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-alt font-semibold text-cass-navy">Xero</h3>
                {p.xero_sync
                  ? <Badge className="bg-emerald-100 text-emerald-700">Synced &#10003;</Badge>
                  : <Badge variant="outline">Not synced</Badge>}
              </div>
              {p.xero_sync ? (
                <div className="space-y-1.5 text-xs text-cass-slate">
                  <div>Invoice reference: <span className="text-cass-navy font-medium">{p.reference}</span></div>
                  <div>Repeating invoice: {p.xero_sync.repeating_invoice_id
                    ? <span className="text-emerald-700 font-medium">Created &#10003;</span>
                    : (p.xero_sync.repeating_error ? <span className="text-red-600">Failed</span> : <span className="text-cass-navy">Not created</span>)}</div>
                  <div>One-off draft invoice: {p.xero_sync.invoice_id
                    ? <span className="text-emerald-700 font-medium">Created &#10003;{p.xero_sync.invoice_number ? ` · ${p.xero_sync.invoice_number}` : ""}</span>
                    : (p.xero_sync.oneoff_error ? <span className="text-red-600">Failed</span> : <span className="text-cass-navy">Not created</span>)}</div>
                  <div>Last action: <span className="text-cass-navy">{p.xero_sync.synced_at ? new Date(p.xero_sync.synced_at).toLocaleString("en-GB") : "\u2014"}</span></div>
                  <div className="text-[11px] text-cass-slate pt-1">Repeating invoices live under <b>Sales &rarr; Invoices &rarr; Repeating</b>; the one-off draft under <b>Sales &rarr; Invoices &rarr; Draft</b>.</div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={openXeroPreview} data-testid="xero-resync-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Create / review</Button>
                    <a href="https://go.xero.com/RepeatingInvoices/" target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid="xero-open-btn">Open in Xero</Button></a>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-cass-slate">Not yet created. Review and create the Xero repeating and/or one-off draft invoice.</p>
                  <Button size="sm" className="bg-cass-navy text-white" onClick={openXeroPreview} data-testid="xero-cardsync-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Create Xero Invoices</Button>
                </div>
              )}
            </Card>
          )}

          {(p.delivery_status || sentEmails.length > 0 || (p.activity || []).some((a) => a.event === "sent" || a.event.startsWith("email_"))) && (
            <Card className="p-6 rounded-md" data-testid="email-timeline-card">
              <h3 className="font-alt font-semibold text-cass-navy mb-3">Email delivery</h3>
              <div className="space-y-3">
                {(p.activity || [])
                  .filter((a) => a.event === "sent" || a.event.startsWith("email_"))
                  .map((a, i) => {
                    const meta = EMAIL_EVENTS[a.event] || { label: a.event.replace(/_/g, " "), Icon: Mail, tone: "text-cass-slate" };
                    const Icon = meta.Icon;
                    return (
                      <div key={i} className="flex items-start gap-3 text-xs" data-testid={`email-event-${a.event}`}>
                        <Icon className={`w-4 h-4 mt-0.5 ${meta.tone}`} />
                        <div>
                          <span className="text-cass-navy font-medium">{meta.label}</span>
                          {a.to ? <span className="text-cass-slate"> · {a.to}</span> : null}
                          <div className="text-cass-slate">{new Date(a.at).toLocaleString("en-GB")}</div>
                        </div>
                      </div>
                    );
                  })}
                {!(p.activity || []).some((a) => a.event.startsWith("email_")) && (
                  <p className="text-[11px] text-cass-slate">Delivered/opened status appears here as the email provider reports it.</p>
                )}
                {sentEmails.length > 0 && (
                  <div className="pt-3 mt-1 border-t border-border">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate/70 mb-2">Sent emails · view exact content</p>
                    <div className="space-y-2">
                      {sentEmails.map((em) => (
                        <div key={em.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2" data-testid={`sent-email-${em.id}`}>
                          <div className="min-w-0">
                            <div className="text-xs text-cass-navy font-medium truncate">{em.event}<span className="font-normal text-cass-slate"> · {em.subject}</span></div>
                            <div className="text-[11px] text-cass-slate truncate">{em.to_name ? `${em.to_name} · ` : ""}{em.to} · {new Date(em.at).toLocaleString("en-GB")}{em.delivered ? "" : " · not delivered"}</div>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => setViewEmail(em)} data-testid={`view-sent-email-${em.id}`}><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card className="p-6 rounded-md">
            <h3 className="font-alt font-semibold text-cass-navy mb-4">Activity</h3>
            <ActivityTimeline activity={p.activity || []} />
          </Card>

          <ClearanceCard proposalId={id} proposalStatus={p.status} />
        </div>
      </div>
      <Dialog open={!!viewEmail} onOpenChange={(o) => { if (!o) { setViewEmail(null); setResendForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-sent-email-dialog">
          <DialogHeader>
            <DialogTitle>Sent email</DialogTitle>
          </DialogHeader>
          {viewEmail && (
            <div className="space-y-3">
              <div className="text-xs text-cass-slate space-y-0.5">
                <div><span className="font-medium text-cass-navy">To:</span> {viewEmail.to_name ? `${viewEmail.to_name} · ` : ""}{viewEmail.to}</div>
                <div><span className="font-medium text-cass-navy">Subject:</span> {viewEmail.subject}</div>
                <div><span className="font-medium text-cass-navy">Sent:</span> {new Date(viewEmail.at).toLocaleString("en-GB")}{viewEmail.delivered ? "" : " · not delivered"}</div>
              </div>
              <iframe title="sent-email" srcDoc={viewEmail.html} className="w-full h-[60vh] border border-border rounded-md bg-white" data-testid="sent-email-iframe" />
              {!resendForm ? (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => setResendForm({ to: viewEmail.to || "", name: viewEmail.to_name || "", subject: viewEmail.subject || "" })} data-testid="resend-from-copy-btn">
                    <Send className="w-3.5 h-3.5 mr-1" />Resend this email
                  </Button>
                </div>
              ) : (
                <div className="border-t border-border pt-3 space-y-2" data-testid="resend-copy-form">
                  <p className="text-[11px] text-cass-slate">Re-send this exact email. Adjust the recipient or subject if needed — the content stays the same.</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div><label className="text-[11px] text-cass-slate">Recipient email</label><Input className="h-9" value={resendForm.to} onChange={(e) => setResendForm({ ...resendForm, to: e.target.value })} data-testid="resend-copy-email" /></div>
                    <div><label className="text-[11px] text-cass-slate">Recipient name</label><Input className="h-9" value={resendForm.name} onChange={(e) => setResendForm({ ...resendForm, name: e.target.value })} data-testid="resend-copy-name" /></div>
                  </div>
                  <div><label className="text-[11px] text-cass-slate">Subject</label><Input className="h-9" value={resendForm.subject} onChange={(e) => setResendForm({ ...resendForm, subject: e.target.value })} data-testid="resend-copy-subject" /></div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button size="sm" variant="ghost" onClick={() => setResendForm(null)} data-testid="resend-copy-cancel">Cancel</Button>
                    <Button size="sm" className="bg-cass-navy text-white" disabled={resendBusy} onClick={doResendCopy} data-testid="resend-copy-send">
                      <Send className="w-3.5 h-3.5 mr-1" />{resendBusy ? "Sending…" : "Send"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tailor-scheds-dialog">
          <DialogHeader><DialogTitle>Tailor service schedules — this proposal only</DialogTitle></DialogHeader>
          <p className="text-sm text-cass-slate">Edits here apply only to this client's Letter of Engagement and never change the master schedules.</p>
          {schedAvail.length > 0 && (
            <div className="mt-3 border border-border rounded-md p-3" data-testid="sched-membership">
              <div className="text-xs font-semibold text-cass-slate uppercase mb-2">Included schedules</div>
              <div className="space-y-1.5">
                {schedAvail.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`sched-toggle-${s.id}`}>
                    <Checkbox checked={schedActive.has(s.id)} onCheckedChange={() => toggleSchedActive(s.id)} disabled={schedLocked} data-testid={`sched-check-${s.id}`} />
                    <span className="text-cass-navy">{s.name}</span>
                    {s.auto ? <Badge variant="outline" className="text-[10px]">auto</Badge> : <Badge variant="outline" className="text-[10px] text-cass-teal border-cass-teal">manual</Badge>}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-cass-slate mt-2">Tick to include a schedule in this client's LoE, untick to remove. Newly added schedules use the master wording — save and reopen to fine-tune it below.</p>
            </div>
          )}
          <div className="space-y-4 mt-2">
            {scheds.length === 0 && <p className="text-sm text-cass-slate">No service schedules apply to this proposal's services.</p>}
            {scheds.map((s, i) => (
              <div key={s.id} data-testid={`tailor-sched-${s.id}`}>
                <label className="text-sm font-medium text-cass-navy">{s.name}</label>
                <Textarea rows={6} value={s.html} onChange={(e) => setScheds((xs) => xs.map((x, j) => (j === i ? { ...x, html: e.target.value } : x)))} className="mt-1 font-mono text-xs" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSchedOpen(false)}>Cancel</Button>
            <Button className="bg-cass-navy text-white" onClick={saveScheds} data-testid="save-scheds-btn">Save for this proposal</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!checks} onOpenChange={(o) => !o && setChecks(null)}>
        <DialogContent data-testid="checks-dialog">
          <DialogHeader><DialogTitle>Pre-send checks</DialogTitle></DialogHeader>
          {checks && (checks.warnings.length === 0
            ? <p className="text-emerald-600 text-sm">All good — no issues found. Ready to send.</p>
            : <div className="space-y-2 max-h-[60vh] overflow-y-auto">{checks.warnings.map((w, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded border ${w.level === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid={`check-${i}`}><b className="capitalize">{w.level}</b> · {w.page}: {w.message}</div>
              ))}</div>)}
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-accept-dialog">
          <DialogHeader><DialogTitle>Mark proposal as accepted</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-cass-slate">Record an internal/manual acceptance (e.g. verbal, email, or a signed paper copy). This converts the client, sets the agreed fees & renewal date, creates onboarding{manual.mark_loe_signed ? ", marks the Letter of Engagement signed," : ""} and syncs to Xero per your settings. Recorded against <b className="text-cass-navy">{user?.name}</b>.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-cass-slate">Client contact name *</label>
                <Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="e.g. Jane Smith" data-testid="manual-name" />
              </div>
              <div>
                <label className="text-xs text-cass-slate">Job title</label>
                <Input value={manual.job_title} onChange={(e) => setManual({ ...manual, job_title: e.target.value })} placeholder="e.g. Director" data-testid="manual-jobtitle" />
              </div>
            </div>
            <div>
              <label className="text-xs text-cass-slate">Company</label>
              <Input value={manual.company} onChange={(e) => setManual({ ...manual, company: e.target.value })} placeholder={p.client_name} data-testid="manual-company" />
            </div>
            <div>
              <label className="text-xs text-cass-slate">How was it accepted? (reason) *</label>
              <Textarea value={manual.reason} onChange={(e) => setManual({ ...manual, reason: e.target.value })} placeholder="e.g. Client returned a signed paper copy on 15/06/2026" data-testid="manual-reason" />
            </div>
            <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer">
              <Checkbox checked={manual.mark_loe_signed} onCheckedChange={(v) => setManual({ ...manual, mark_loe_signed: !!v })} data-testid="manual-loe-check" />
              Also mark the Letter of Engagement as signed
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setManualOpen(false)} data-testid="manual-cancel">Cancel</Button>
              <Button onClick={submitManualAccept} disabled={manualBusy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="manual-confirm">{manualBusy ? "Saving…" : "Confirm acceptance"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
